import React, { useEffect, useState } from 'react';
import {
  Box, Button, Chip, FormControl, IconButton, MenuItem, Select, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Tooltip, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EmojiEventsOutlinedIcon from '@mui/icons-material/EmojiEventsOutlined';
import AnnouncementFormModal from './AnnouncementFormModal';
import AnnouncementDetailModal from './AnnouncementDetailModal';
import LocalText from '../common/LocalText';
import ConfirmModal from '../form-components/ConfirmModal';
import { useToast } from '../../context/ToastContext';
import { useLocalization } from '../../context/LocalizationContext';
import {
  createAnnouncement, getAnnouncements, updateAnnouncement, deleteAnnouncement,
} from '../../api/gameAnnouncementApi';
import { getGames } from '../../api/gameScoreBoardApi';
import { resolveGameName, filterLabelSx, selectSx } from '../gameScoreBoard/ScoreboardFilterBar';
import { C, btnBase, ctaNavy } from '../../theme/adminTokens';
import { announcementQuizzes, isQuizAnnouncement } from '../../utils/gameSchedule';

import { isScheduledGame } from '../../constants/gameSchedule';
const headCellSx = {
  fontSize: 11,
  fontWeight: 700,
  color: C.muted,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  borderBottom: `1px solid ${C.border}`,
  borderRight: `1px solid ${C.border}`,
  '&:last-of-type': { borderRight: 'none' },
  whiteSpace: 'nowrap',
  // Without this, a header narrower than its own nowrap text doesn't wrap or clip — it
  // paints straight over the neighboring header cell instead (the garbled/overlapping
  // header look on narrow screens).
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  py: 1.5,
  px: 2,
};

const bodyCellSx = {
  fontSize: 13,
  color: '#1e293b',
  borderBottom: `1px solid ${C.border}`,
  borderRight: `1px solid ${C.border}`,
  '&:last-of-type': { borderRight: 'none' },
  py: 1.5,
  px: 2,
  verticalAlign: 'middle',
};

// Truncates long single-line text instead of letting it wrap the row to many lines (or, at
// narrow widths with tableLayout:'fixed', overflow the cell) — used for Name and Period,
// both of which can be long free-text/date-range strings.
const truncateCellSx = {
  ...bodyCellSx,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const TYPE_LABEL = { weekly: 'Weekly', monthly: 'Monthly', custom: 'Custom' };

const WINNERS_DELETE_TIP = "Winners were already granted, so this announcement can't be deleted";

// Date-only strings (.NET DateOnly, e.g. '2026-09-01') parse as UTC midnight — appending
// T00:00:00 forces local-midnight parsing instead, same guard used elsewhere in this
// codebase (AdvertiserAdTable.jsx, AdScheduleCalendarPicker.jsx) to avoid the date
// rendering a day early for anyone west of UTC.
const fmtDateOnly = (d) => (d ? new Date(`${d}T00:00:00`).toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' }) : '');

const describePeriod = (a) => {
  // Same PascalCase-vs-lowercase mismatch as AnnouncementFormModal.jsx: the API returns
  // "Weekly"/"Monthly"/"Custom", not the lowercase literals TYPE_LABEL is keyed by.
  const type = String(a.announcementType ?? '').toLowerCase();
  const label = TYPE_LABEL[type] ?? a.announcementType;
  // A Quiz announcement's dates come from its quizzes (earliest start .. latest end), so they are always shown.
  if ((type === 'custom' || isQuizAnnouncement(a)) && a.startDate && a.endDate) {
    return `${label} (${fmtDateOnly(a.startDate)} – ${fmtDateOnly(a.endDate)})`;
  }
  return label;
};

/**
 * Announcements list + create/edit/delete, for whichever game THIS section's own Game
 * selector has selected. Sibling section to GameScoreboardSection, placed below it on
 * GameScoreBoard.jsx — deliberately independent: each section owns its own Game dropdown
 * and its own selected game, they no longer share one selection between tabs.
 */
export default function GameAnnouncementSection({ canManage = false }) {
  const { toast } = useToast();
  const { locale } = useLocalization();

  const [games, setGames] = useState([]);
  const [gamesLoading, setGamesLoading] = useState(true);
  const [gameId, setGameId] = useState(null);

  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);

  const [formOpen, setFormOpen] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] = useState(null);

  const [detailAnnouncement, setDetailAnnouncement] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  // Own game catalog + default-to-first-active-game, same pattern as GameScoreboardSection's
  // own copy — deliberately duplicated rather than shared, since the whole point is that
  // these two selectors no longer need to agree with each other.
  useEffect(() => {
    let cancelled = false;
    setGamesLoading(true);
    getGames()
      .then((data) => {
        if (cancelled) return;
        const list = Array.isArray(data) ? data : [];
        setGames(list);
        const firstActive = list.find((g) => g.isActive) ?? list[0];
        if (firstActive) setGameId(firstActive.id);
      })
      .catch(() => { if (!cancelled) toast.error('Failed to load the games list.'); })
      .finally(() => { if (!cancelled) setGamesLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Switching games while a modal referencing the OLD game's announcement is open would
  // leave it showing stale data pointed at the wrong game — close them rather than let that
  // happen silently.
  const handleGameChange = (nextGameId) => {
    setGameId(nextGameId);
    setFormOpen(false);
    setEditingAnnouncement(null);
    setDetailAnnouncement(null);
    setDeleteTarget(null);
  };

  const refresh = () => {
    if (!gameId) {
      // No game selected (e.g. a space with zero configured games) — nothing to load,
      // and without this the section would stay stuck on "Loading…" forever, since
      // `loading` starts true and nothing else would ever flip it back off.
      setAnnouncements([]);
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    return getAnnouncements(gameId)
      .then((data) => setAnnouncements(Array.isArray(data) ? data : []))
      .catch(() => toast.error('Failed to load announcements.'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameId]);

  const handleCreateClick = () => {
    setEditingAnnouncement(null);
    setFormOpen(true);
  };

  const handleEditClick = (announcement) => {
    setEditingAnnouncement(announcement);
    setFormOpen(true);
  };

  const handleFormSave = async (payload) => {
    try {
      if (editingAnnouncement) {
        await updateAnnouncement(gameId, editingAnnouncement.id, { ...payload, status: editingAnnouncement.status });
        toast.success('Announcement updated.');
      } else {
        await createAnnouncement(gameId, payload);
        toast.success('Announcement created.');
      }
      setFormOpen(false);
      setEditingAnnouncement(null);
      await refresh();
    } catch (_) {
      toast.error('Failed to save the announcement.');
    }
  };

  const handleDelete = async (announcement) => {
    setDeleteTarget(null);
    try {
      await deleteAnnouncement(gameId, announcement.id);
      toast.success('Announcement deleted.');
      await refresh();
    } catch (_) {
      toast.error('Failed to delete the announcement.');
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', justifyContent: 'space-between', gap: 1.5, mb: 2 }}>
        <Box sx={{ width: { xs: '100%', sm: 'auto' } }}>
          <Typography id="announcement-game-label" sx={filterLabelSx}>
            <LocalText>Game</LocalText>
          </Typography>
          <FormControl size="small" disabled={gamesLoading || games.length === 0} sx={{ width: { xs: '100%', sm: 'auto' } }}>
            <Select
              value={gameId ?? ''}
              onChange={(e) => handleGameChange(e.target.value)}
              displayEmpty
              sx={selectSx}
              labelId="announcement-game-label"
              inputProps={{ 'aria-label': 'Select a game' }}
              renderValue={(v) => {
                if (!v) return <Typography sx={{ color: '#94a3b8', fontSize: 13 }}><LocalText>Select a game</LocalText></Typography>;
                const g = games.find((game) => game.id === v);
                return <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#1e293b' }}>{resolveGameName(g, locale)}</Typography>;
              }}
            >
              {games.map((g) => (
                <MenuItem key={g.id} value={g.id}>{resolveGameName(g, locale)}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon sx={{ fontSize: 16 }} />}
          onClick={handleCreateClick}
          disabled={!gameId || !canManage}
          sx={{ ...btnBase, ...ctaNavy, width: { xs: '100%', sm: 'auto' } }}
        >
          <LocalText>New Announcement</LocalText>
        </Button>
      </Box>

      {loading ? (
        <Typography variant="body2" role="status" sx={{ color: C.muted }}>
          <LocalText>Loading…</LocalText>
        </Typography>
      ) : announcements.length === 0 ? (
        <Typography variant="body2" role="status" sx={{ color: C.muted }}>
          <LocalText>No announcements yet for this game.</LocalText>
        </Typography>
      ) : (
        <TableContainer sx={{ overflowX: 'auto', border: `1px solid ${C.border}`, borderRadius: '8px' }}>
          <Table
            size="small"
            aria-label="Game announcements"
            sx={{
              // Fixed % column widths work well once there's enough room for all 5 (Name/
              // Period/Announcement Date/Top Count/Actions) to breathe — but on a narrow
              // phone screen there just isn't enough width to go around, and forcing fixed
              // layout there squeezes headers and the Actions button/icons past their own
              // cell boundaries into the next column. Below `sm`, fall back to auto layout
              // with a real minWidth, so the TableContainer's existing overflowX:'auto'
              // does an honest horizontal scroll instead.
              tableLayout: { xs: 'auto', sm: 'fixed' },
              minWidth: { xs: 640, sm: 'auto' },
              '& tbody tr:last-of-type td': { borderBottom: 'none' },
            }}
          >
            <TableHead>
              <TableRow sx={{ bgcolor: '#f1f5f9' }}>
                <TableCell scope="col" sx={{ ...headCellSx, width: '20%' }} align="center"><LocalText>Name</LocalText></TableCell>
                <TableCell scope="col" sx={{ ...headCellSx, width: '26%' }} align="center"><LocalText>Period</LocalText></TableCell>
                <TableCell scope="col" sx={{ ...headCellSx, width: '16%' }} align="center"><LocalText>Announcement Date</LocalText></TableCell>
                <TableCell scope="col" sx={{ ...headCellSx, width: '12%' }} align="center"><LocalText>Top Count</LocalText></TableCell>
                <TableCell scope="col" sx={{ ...headCellSx, width: '26%' }} align="center"><LocalText>Actions</LocalText></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {announcements.map((a) => {
                const quizzes = announcementQuizzes(a);
                // Once winners are granted, a Quiz announcement can't be deleted (the server refuses it too).
                const deleteLocked = quizzes.length > 0 && Boolean(a.hasWinners);
                return (
                  <TableRow
                    key={a.id}
                    hover
                    // Explicit hover color — stays in the same neutral gray family as the
                    // header (not blue), but lighter than it (#f8fafc vs the header's
                    // #f1f5f9), so a hovered row reads as "slightly off-white", distinctly
                    // paler than the header band rather than a shade toward/matching it.
                    sx={{ '&:hover': { bgcolor: '#f8fafc' } }}
                  >
                    <TableCell sx={{ ...truncateCellSx, fontWeight: 700 }}>
                      {a.name || `Announcement #${a.id}`}
                      {quizzes.length > 0 && (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                          {quizzes.map((q) => (
                            <Chip
                              key={q.id ?? q.name}
                              size="small"
                              label={q.name}
                              sx={{ height: 20, fontSize: 11, fontWeight: 500, bgcolor: '#eff6ff', color: C.navy, maxWidth: '100%' }}
                            />
                          ))}
                        </Box>
                      )}
                    </TableCell>
                    <TableCell sx={truncateCellSx}>{describePeriod(a)}</TableCell>
                    <TableCell sx={{ ...bodyCellSx, fontWeight: 700 }} align="center">{fmtDateOnly(a.announcementDate) || '—'}</TableCell>
                    <TableCell sx={bodyCellSx} align="center">{a.topCount}</TableCell>
                    <TableCell sx={bodyCellSx}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2.5 }}>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<EmojiEventsOutlinedIcon sx={{ fontSize: 14 }} />}
                          onClick={() => setDetailAnnouncement(a)}
                          sx={{
                            ...btnBase,
                            fontSize: 11.5,
                            borderColor: C.border,
                            color: C.navy,
                            '&:hover': { borderColor: C.navy, bgcolor: '#eff6ff' },
                          }}
                        >
                          <LocalText>View Winners</LocalText>
                        </Button>
                        {canManage && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <IconButton size="small" onClick={() => handleEditClick(a)} aria-label="Edit announcement">
                              <EditOutlinedIcon sx={{ fontSize: 16, color: C.muted }} />
                            </IconButton>
                            <Tooltip title={deleteLocked ? WINNERS_DELETE_TIP : ''}>
                              <span>
                                <IconButton size="small" onClick={() => setDeleteTarget(a)} disabled={deleteLocked} aria-label="Delete announcement">
                                  <DeleteOutlineIcon sx={{ fontSize: 16, color: deleteLocked ? undefined : C.muted }} />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </Box>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {formOpen && (
        <AnnouncementFormModal
          open={formOpen}
          onClose={() => { setFormOpen(false); setEditingAnnouncement(null); }}
          onSave={handleFormSave}
          initialData={editingAnnouncement}
          gameId={gameId}
          scheduled={isScheduledGame(games.find((g) => g.id === gameId))}
        />
      )}

      {detailAnnouncement && (
        <AnnouncementDetailModal
          open={Boolean(detailAnnouncement)}
          onClose={() => setDetailAnnouncement(null)}
          gameId={gameId}
          announcement={detailAnnouncement}
          canManage={canManage}
          onGranted={refresh}
        />
      )}

      <ConfirmModal
        open={Boolean(deleteTarget)}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={() => handleDelete(deleteTarget)}
        severity="danger"
        title="Delete Announcement"
        message={deleteTarget ? `Delete "${deleteTarget.name || `Announcement #${deleteTarget.id}`}"?` : ''}
        subtitle="This removes the announcement and cannot be undone."
        confirmLabel="Delete"
      />
    </Box>
  );
}
